/**
 * Subcontractor packets, from the office's side (ADR 0279).
 *
 * The office makes the row, which is what makes the link, and records when
 * the letter carrying it went; everything after that — opening, signing,
 * filing the PDF — is the `crew-packet` Edge
 * Function's, because the subcontractor has no login. Row level security has
 * no update policy on this table for exactly that reason.
 */

import type { CrewPacketLanguage, CrewPacketRow } from "@roof-hawk/core";

import { supabase } from "./supabase";

export interface SentCrewPacket extends CrewPacketRow {
  id: string;
  language: CrewPacketLanguage;
  sent_to: string | null;
  signer_name: string | null;
}

/** A new packet for this crew, and the token its link is built from. */
export async function createCrewPacket(input: {
  crewId: string;
  language: CrewPacketLanguage;
  sentTo: string | null;
}): Promise<string> {
  const { data, error } = await supabase
    .from("crew_packets")
    .insert({
      crew_id: input.crewId,
      language: input.language,
      sent_to: input.sentTo?.trim() || null,
    })
    .select("token")
    .single();
  if (error) throw new Error(error.message);
  return (data as { token: string }).token;
}

/**
 * The letter carrying this packet's link went, sent by Gmail or copied to be
 * sent another way, to `to` as it stood on the letter. Until this runs, the
 * packet reads "Link made, not sent".
 */
export async function markCrewPacketSent(
  token: string,
  to: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("crew_packet_was_sent", {
    p_token: token,
    p_to: to ?? "",
  });
  if (error) throw new Error(error.message);
}

/** The packets already sent to this crew, newest first. */
export async function listCrewPackets(
  crewId: string,
): Promise<SentCrewPacket[]> {
  const { data, error } = await supabase
    .from("crew_packets")
    .select(
      "id, language, sent_to, created_at, sent_at, expires_at, opened_at, signed_at, signer_name",
    )
    .eq("crew_id", crewId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data as SentCrewPacket[] | null) ?? [];
}
